import { NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';
import { LayoutComponent } from './core/layout/layout.component';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { StoreModule } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { environment } from '../environments/environment';
import { AuthGuard } from './core/guards/auth/auth.guard';
import { httpInterceptorProviders } from './core/interceptors';
import { AuthStoreModule } from './auth/store/auth-store.module';
import { generalReducers } from './+state/general.reducers';
import { GENERAL_FEATURE_NAME } from './+state/general.actions';
import { ColumnsService } from './services/columns.service';
import { GeneralEffects } from './+state/general.effects';
import { registerLocaleData } from '@angular/common';
import { Locales } from './enums/locales.enum';
import localeEn from '@angular/common/locales/en';
import localeUk from '@angular/common/locales/uk';
import { ROLES_FEATURE_NAME } from './+state/roles/roles.actions';
import { rolesReducers } from './+state/roles/roles.reducers';
import { RolesEffects } from './+state/roles/roles.effects';
import { ScopesService } from './services/scopes.service';
import { ScopesResolver } from './services/resolvers/scopes.resolver';
import { SCOPES_FEATURE_NAME } from './+state/scopes/scopes.actions';
import { scopesReducers } from './+state/scopes/scopes.reducers';
import { ScopesFacade } from './+state/scopes/scopes.facade';
import { PermissionsService } from './services/permissions.service';
import { UserPortfolioEffects } from './+state/user-portfolio/user-portfolio.effects';
import { USER_PORTFOLIO_FEATURE_NAME } from './+state/user-portfolio/user-portfolio.actions';
import { userPortfolioReducers } from './+state/user-portfolio/user-portfolio.reducers';
import { DataService } from './pages/users-management/services/data.service';
import { PermissionsResolver } from './services/resolvers/permissions.resolver';
import { ColumnsAliasesResolver } from './services/resolvers/columns-aliases.resolver';
import { ColumnsResolver } from './services/resolvers/columns.resolver';
import { ColumnsAliasesService } from './services/columns-aliases.service';
import { COLUMNS_ALIASES_STATE_NAME } from './+state/column-aliases/interfaces/columns-aliases.state.interface';
import { columnsAliasesReducers } from './+state/column-aliases/columns-aliases.reducers';
import { ColumnsAliasesEffects } from './+state/column-aliases/columns-aliases.effects';
import { OtpService } from './services/otp.service';
import { ErrorComponent } from './errors/error/error.component';
import { NotFoundComponent } from './errors/not-found/not-found.component';
import { AccessDeniedComponent } from './errors/access-denied/access-denied.component';
import { ChangePasswordModule } from './modals/change-password/change-password.module';
import { ProfileModule } from './modals/profile/profile.module';
import { ReactiveFormsModule } from '@angular/forms';
import { OtpSetupModule } from './modals/otp-setup/otp-setup.module';
import {
  CellValueFormatterPipe,
} from './pages/investigations/overall/modules/overall-cell-renderers/pipes/cell-value-formatter.pipe';
import { NotificationsComponent } from './notifications/notifications.component';
import { CsvUtilsService } from './services/csv-utils.service';
import { customHttpLoaderFactory } from './core/translation/customTranslateLoader';

import { HighlightTextModule } from './core/pipes/highlight-text/highlight-text.module';
import { ApiUrlService } from './services/api-url.service';
import { IconComponent, NavBarComponent, RadioComponent, TagComponent, ToastComponent, ModalService } from '@frogo-kit/core';

registerLocaleData(localeEn, Locales.EN);
registerLocaleData(localeUk, Locales.UA);

@NgModule({
  declarations: [
    AppComponent,
    LayoutComponent,
    NotificationsComponent,
    AccessDeniedComponent,
    ErrorComponent,
    NotFoundComponent,
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    HttpClientModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: customHttpLoaderFactory,
        deps: [HttpClient],
      },
      isolate: false,
    }),
    StoreModule.forRoot(
      {
        [GENERAL_FEATURE_NAME]: generalReducers,
        [ROLES_FEATURE_NAME]: rolesReducers,
        [SCOPES_FEATURE_NAME]: scopesReducers,
        [USER_PORTFOLIO_FEATURE_NAME]: userPortfolioReducers,
        [COLUMNS_ALIASES_STATE_NAME]: columnsAliasesReducers,
      },
      {
        metaReducers: !environment.production ? [] : [],
        runtimeChecks: {
          strictActionImmutability: true,
          strictStateImmutability: true,
        },
      },
    ),
    AuthStoreModule,
    EffectsModule.forRoot([GeneralEffects, ColumnsAliasesEffects, RolesEffects, UserPortfolioEffects]),
    !environment.production ? StoreDevtoolsModule.instrument() : [],
    ChangePasswordModule,
    OtpSetupModule,
    HighlightTextModule,
    ProfileModule,
    ReactiveFormsModule,
    IconComponent,
    TagComponent,
    RadioComponent,
    NavBarComponent,
    ToastComponent,
  ],
  providers: [
    ApiUrlService,
    AuthGuard,
    PermissionsService,
    ScopesService,
    DataService,
    ScopesResolver,
    ScopesFacade,
    ColumnsService,
    ColumnsAliasesService,
    ColumnsResolver,
    ColumnsAliasesResolver,
    PermissionsResolver,
    OtpService,
    httpInterceptorProviders,
    CellValueFormatterPipe,
    ModalService,
    CsvUtilsService,
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
